import { useEffect, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { haptik } from "../lib/mobil";

export interface WischAktion {
  label: string;
  icon: LucideIcon;
  /** CSS-Farbe der aufgedeckten Fläche. */
  farbe: string;
  /** Nimmt die Zeile die Mail aus der Liste? Dann fliegt sie hinaus statt zurückzuschnappen. */
  entfernt?: boolean;
  aktion: () => Promise<unknown> | void;
}

const SCHWELLE = 84;

/**
 * Zeile mit Wischgesten wie in Mail-Apps: nach rechts und nach links je eine
 * Aktion, langes Drücken für die Mehrfachauswahl. Nur Berührung — Maus und
 * Trackpad bleiben beim Klicken und Rechtsklick.
 *
 * Die Richtung entscheidet sich nach den ersten Pixeln: überwiegend senkrecht
 * heißt Blättern, dann hält sich die Zeile raus.
 */
export function WischZeile({ children, rechts, links, aktiv, onLangDruck }: {
  children: ReactNode;
  /** Aufgedeckt beim Wischen nach rechts. */
  rechts?: WischAktion;
  /** Aufgedeckt beim Wischen nach links. */
  links?: WischAktion;
  aktiv: boolean;
  onLangDruck?: () => void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const [dx, setDx] = useState(0);
  const [phase, setPhase] = useState<"ruhe" | "zieht" | "zurueck" | "raus">("ruhe");
  const aktuell = useRef({ rechts, links, onLangDruck });
  aktuell.current = { rechts, links, onLangDruck };

  useEffect(() => {
    const el = box.current;
    if (!el || !aktiv) return;
    let start: { x: number; y: number } | null = null;
    let modus: "offen" | "waagrecht" | "senkrecht" = "offen";
    let weite = 0;
    let ueberSchwelle = false;
    let langTimer: number | undefined;
    // Nach einer Geste darf der folgende Klick die Mail nicht öffnen.
    let klickSchlucken = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      start = { x: t.clientX, y: t.clientY };
      modus = "offen";
      weite = 0;
      ueberSchwelle = false;
      klickSchlucken = false;
      window.clearTimeout(langTimer);
      if (aktuell.current.onLangDruck) {
        langTimer = window.setTimeout(() => {
          if (modus !== "offen") return;
          klickSchlucken = true;
          start = null;
          haptik(18);
          aktuell.current.onLangDruck?.();
        }, 480);
      }
    };

    const onMove = (e: TouchEvent) => {
      if (!start) return;
      const t = e.touches[0]!;
      const mx = t.clientX - start.x;
      const my = t.clientY - start.y;
      if (modus === "offen") {
        if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
        window.clearTimeout(langTimer);
        modus = Math.abs(mx) > Math.abs(my) * 1.4 ? "waagrecht" : "senkrecht";
        if (modus === "waagrecht") setPhase("zieht");
      }
      if (modus !== "waagrecht") return;
      e.preventDefault();
      const erlaubt = mx > 0 ? aktuell.current.rechts : aktuell.current.links;
      // Ohne Aktion in dieser Richtung: nur ein kleiner, zäher Ausschlag.
      weite = erlaubt ? mx : mx / 6;
      if (Math.abs(weite) > SCHWELLE * 1.6) weite = Math.sign(weite) * (SCHWELLE * 1.6 + (Math.abs(weite) - SCHWELLE * 1.6) / 4);
      const jetzt = Boolean(erlaubt) && Math.abs(weite) >= SCHWELLE;
      if (jetzt !== ueberSchwelle) { ueberSchwelle = jetzt; haptik(8); }
      setDx(weite);
    };

    const onEnd = () => {
      window.clearTimeout(langTimer);
      if (modus !== "waagrecht") { start = null; return; }
      klickSchlucken = true;
      start = null;
      const a = weite > 0 ? aktuell.current.rechts : aktuell.current.links;
      if (a && Math.abs(weite) >= SCHWELLE) {
        if (a.entfernt) {
          setPhase("raus");
          setDx(Math.sign(weite) * el.offsetWidth);
        } else {
          setPhase("zurueck");
          setDx(0);
        }
        void Promise.resolve(a.aktion()).finally(() => {
          // Bleibt die Zeile nach der Aktion stehen (Fehler, Ansicht behält sie), zurückholen.
          window.setTimeout(() => { setPhase("zurueck"); setDx(0); }, 600);
        });
      } else {
        setPhase("zurueck");
        setDx(0);
      }
    };

    const onClick = (e: MouseEvent) => {
      if (!klickSchlucken) return;
      klickSchlucken = false;
      e.preventDefault();
      e.stopPropagation();
    };
    // Langes Drücken öffnet auf Android sonst zusätzlich das Kontextmenü.
    const onKontext = (e: Event) => { if (klickSchlucken || modus === "offen") e.preventDefault(); };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    el.addEventListener("click", onClick, true);
    el.addEventListener("contextmenu", onKontext, true);
    return () => {
      window.clearTimeout(langTimer);
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
      el.removeEventListener("click", onClick, true);
      el.removeEventListener("contextmenu", onKontext, true);
    };
  }, [aktiv]);

  const sichtbar = dx > 0 ? rechts : dx < 0 ? links : undefined;
  const Icon = sichtbar?.icon;

  return (
    <div className="wisch" ref={box}>
      {sichtbar && Icon && (
        <div
          className="wisch-grund"
          data-seite={dx > 0 ? "links" : "rechts"}
          data-scharf={Math.abs(dx) >= SCHWELLE}
          style={{ ["--wisch-farbe" as string]: sichtbar.farbe }}
          aria-hidden="true"
        >
          <Icon />
          <span>{sichtbar.label}</span>
        </div>
      )}
      <div
        className="wisch-inhalt"
        data-phase={phase}
        style={dx !== 0 ? { transform: `translate3d(${dx}px,0,0)` } : undefined}
        onTransitionEnd={() => { if (phase === "zurueck") setPhase("ruhe"); }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Nach unten ziehen, wenn die Liste ganz oben steht, lädt neu. Gibt die
 * aktuelle Zugweite zurück, damit die Liste den Kringel zeichnen kann.
 */
export function useZiehenAktualisieren(
  scroller: React.RefObject<HTMLElement | null>,
  onRefresh: () => void,
  aktiv: boolean,
): { weite: number; laedt: boolean } {
  const [weite, setWeite] = useState(0);
  const [laedt, setLaedt] = useState(false);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    const el = scroller.current;
    if (!el || !aktiv) return;
    let startY: number | null = null;
    let startX = 0;
    let zug = 0;
    let gemeldet = false;

    const onStart = (e: TouchEvent) => {
      startY = el.scrollTop <= 0 && e.touches.length === 1 ? e.touches[0]!.clientY : null;
      startX = e.touches[0]?.clientX ?? 0;
      zug = 0;
      gemeldet = false;
    };
    const onMove = (e: TouchEvent) => {
      if (startY === null) return;
      const dy = e.touches[0]!.clientY - startY;
      // Seitliches Wischen gehört der Zeile, nicht dem Neuladen.
      if (zug === 0 && Math.abs(e.touches[0]!.clientX - startX) > Math.abs(dy)) { startY = null; return; }
      if (dy <= 0 || el.scrollTop > 0) { if (zug) { zug = 0; setWeite(0); } return; }
      e.preventDefault();
      zug = Math.min(120, dy * 0.5);
      if (zug >= 64 && !gemeldet) { gemeldet = true; haptik(10); }
      if (zug < 64) gemeldet = false;
      setWeite(zug);
    };
    const onEnd = () => {
      if (startY === null) return;
      startY = null;
      if (zug >= 64) {
        setLaedt(true);
        setWeite(52);
        refresh.current();
        window.setTimeout(() => { setLaedt(false); setWeite(0); }, 900);
      } else {
        setWeite(0);
      }
      zug = 0;
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [scroller, aktiv]);

  return { weite, laedt };
}
